// Alpa Feedback — widget de retours, réservé aux utilisateurs authentifiés.
//
// Le widget ne doit jamais apparaître pour un visiteur d'exposition. Le critère
// est l'authentification Firebase, et rien d'autre : un visiteur n'a pas de
// session, donc rien ne se monte, et le fichier du widget n'est même pas
// téléchargé — il n'est injecté qu'au premier besoin réel.
//
// ⚠️ NE JAMAIS DÉMONTER SUR UN CHANGEMENT DE ROUTE.
//
// Une liste d'écrans d'administration a vécu ici jusqu'au 2026-09-21 : le
// widget se démontait dès qu'on la quittait. Elle partait d'une bonne intention
// — protéger les visiteurs — mais elle prenait la route pour critère là où
// seule l'authentification compte, et elle détruisait à chaque passage le
// panneau ouvert, le brouillon en cours de frappe et surtout l'enregistrement
// vidéo. Or filmer un bogue suppose de naviguer : c'était exactement le geste
// que ce branchement interdisait. Voir « Un seul montage pour toute la vie de
// la page » dans le docs/integration.md de l'outil.
//
// Conséquence assumée : un administrateur connecté voit la pastille partout, y
// compris sur un aperçu d'exposition ou un template. C'est voulu — on signale
// un bogue là où on le voit.
//
// Pourquoi on ne lit pas `auth.currentUser` : au premier passage, Firebase n'a
// pas encore restauré la session — `currentUser` vaut `null` alors que
// l'utilisateur est bel et bien connecté. On s'abonne donc à
// `onAuthStateChanged`, seule source de vérité sur l'état réel.

import { auth } from './main.js';
import { onAuthStateChanged } from 'firebase/auth';

const ORIGINE = process.env.VUE_APP_ALPA_ORIGIN;
const PROJET = process.env.VUE_APP_ALPA_PROJECT;
// La route du jeton peut vivre ailleurs que l'API principale — le temps d'un
// essai en local, par exemple, pendant que l'application reste branchée sur
// l'API de production. À défaut, on prend la base de l'application.
const BASE_JETON = process.env.VUE_APP_ALPA_TOKEN_BASE || process.env.VUE_APP_BASEURL;

let utilisateurCourant = null;
let chargement = null; // promesse de chargement du script, une seule fois
let monte = false;
let utilisateurMonte = null;
let abonne = false;

/** Injecte widget.js à la demande. Les visiteurs ne le téléchargent jamais. */
function chargerWidget() {
  if (chargement) return chargement;
  chargement = new Promise((resolve, reject) => {
    if (window.AlpaFeedback) return resolve();
    const script = document.createElement('script');
    script.src = `${ORIGINE}/widget.js`;
    script.dataset.project = PROJET;
    script.defer = true;
    // Le chargeur signale sa disponibilité par un événement ; on écoute les
    // deux, l'événement pouvant précéder le onload selon le navigateur.
    window.addEventListener('alpa-feedback:ready', () => resolve(), { once: true });
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('widget.js injoignable'));
    document.head.appendChild(script);
  });
  return chargement;
}

/** Échange le jeton Firebase de l'utilisateur courant contre un jeton Alpa. */
async function demanderJeton(utilisateur) {
  if (!utilisateur) return null;
  const idToken = await utilisateur.getIdToken();
  const reponse = await fetch(`${BASE_JETON}/alpa-feedback/token`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!reponse.ok) return null;
  const { token } = await reponse.json();
  return token || null;
}

/** Démontage : à la déconnexion, ou quand un autre utilisateur prend la main. */
function demonter() {
  if (!monte) return;
  try {
    window.AlpaFeedback?.destroy();
  } catch (e) {
    // Le démontage ne doit jamais casser une navigation.
  }
  monte = false;
  utilisateurMonte = null;
}

async function reevaluer() {
  if (!ORIGINE || !PROJET) return; // non configuré : on ne fait rien

  if (!utilisateurCourant) return demonter(); // déconnexion
  if (monte && utilisateurMonte === utilisateurCourant.uid) return; // déjà monté
  if (monte) demonter(); // changement d'utilisateur : on repart proprement

  const utilisateur = utilisateurCourant;
  try {
    const jeton = await demanderJeton(utilisateur);
    // Entre-temps l'utilisateur a pu se déconnecter, ou un autre se connecter.
    if (utilisateurCourant !== utilisateur) return;
    if (!jeton) return; // pas authentifié côté API : on s'abstient, sans bruit
    await chargerWidget();
    if (utilisateurCourant !== utilisateur) return;
    window.AlpaFeedback.init({
      token: jeton,
      onTokenExpired: () => demanderJeton(utilisateur),
    });
    monte = true;
    utilisateurMonte = utilisateur.uid;
  } catch (e) {
    // Un outil de retours qui tombe ne doit jamais emporter l'application.
    monte = false;
  }
}

/**
 * Amorçage, appelé au démarrage de l'application. Les appels suivants ne font
 * rien : c'est l'abonnement à l'état d'authentification qui pilote tout, et il
 * ne doit exister qu'une fois. Surtout, cette fonction ne connaît pas la route
 * courante — c'est la condition pour que le widget survive à la navigation.
 */
export function demarrerAlpaFeedback() {
  if (abonne) return;
  abonne = true;
  onAuthStateChanged(auth, (utilisateur) => {
    utilisateurCourant = utilisateur;
    reevaluer();
  });
}
